import { MapPin } from 'lucide-react';
import { Property } from '@/types/property';
import { useEffect, useRef, useState, memo } from 'react';
import Image from 'next/image';

interface MapViewProps {
    properties: Property[];
    hoveredId: string | null;
    onPropertyHover: (id: string | null) => void;
}

function MapViewComponent({ properties, hoveredId, onPropertyHover }: MapViewProps) {
    const mapRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (mapRef.current) {
            const updateDimensions = () => {
                if (mapRef.current) {
                    setDimensions({
                        width: mapRef.current.offsetWidth,
                        height: mapRef.current.offsetHeight,
                    });
                }
            };

            updateDimensions();
            window.addEventListener('resize', updateDimensions);
            return () => window.removeEventListener('resize', updateDimensions);
        }
    }, []);

    const normalizeCoordinate = (lat: number, lng: number) => {
        // This is a simplified normalization for demo purposes. 
        // In a real app with Google Maps, you'd use the libraries overlay projection.
        // For custom SVG map, this needs to be calibrated to the map bounds.
        // Assuming Dubai-ish bounds for now if not provided ??

        // Actually, let's just keep the existing logic but maybe guard against 0/0 better.
        if (!lat || !lng) return { x: -100, y: -100 };

        const latNorm = ((90 - lat) / 180) * 100;
        const lngNorm = ((lng + 180) / 360) * 100;
        return { x: lngNorm, y: latNorm };
    };

    return (
        <div className="h-full bg-gray-100 relative overflow-hidden" ref={mapRef}>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-emerald-50 to-teal-50">
                <div className="w-full h-full relative">
                    <svg className="w-full h-full opacity-20">
                        <defs>
                            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="gray" strokeWidth="0.5" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#grid)" />
                    </svg>

                    {properties?.map((property) => {
                        if (!property.location?.latitude || !property.location?.longitude) return null;

                        const { x, y } = normalizeCoordinate(property.location?.latitude, property.location?.longitude);
                        const isHovered = hoveredId === (property._id || property.id);

                        return (
                            <div
                                key={property._id || property.id}
                                className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300"
                                style={{
                                    left: `${x}%`,
                                    top: `${y}%`,
                                    zIndex: isHovered ? 50 : 10,
                                }}
                                onMouseEnter={() => onPropertyHover(property._id || property.id || null)}
                                onMouseLeave={() => onPropertyHover(null)}
                            >
                                <div
                                    className={`relative transition-all duration-300 ${isHovered ? 'scale-125' : 'scale-100'
                                        }`}
                                >
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${isHovered
                                            ? 'bg-emerald-600 ring-4 ring-emerald-200'
                                            : 'bg-white border-2 border-emerald-600'
                                            }`}
                                    >
                                        <MapPin
                                            className={`w-5 h-5 transition-colors ${isHovered ? 'text-white' : 'text-emerald-600'
                                                }`}
                                        />
                                    </div>

                                    {isHovered && (
                                        <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-xl p-3 w-56 animate-fadeIn z-50">
                                            <Image
                                                src={property.media?.images?.[0] || property.images?.[0] || '/placeholder.jpg'}
                                                alt={property.title}
                                                className="w-full h-24 object-cover rounded-md mb-2"
                                                width={200}
                                                height={100}
                                            />
                                            <h4 className="font-bold text-sm text-gray-900 line-clamp-1">
                                                {property.title}
                                            </h4>
                                            <p className="text-xs text-gray-600 mt-0.5">{property.location?.address}</p>
                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                                                <span className="text-sm font-bold text-gray-900">
                                                    AED {property.price || property.basic_price || 0}
                                                    <span className="text-xs font-normal text-gray-500"> / night</span>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {!isHovered && (
                                    <div className="absolute top-full mt-1 left-1/2 transform -translate-x-1/2 bg-emerald-900 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap font-medium shadow-md">
                                        AED {property.price || property.basic_price || 0}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 text-xs">
                <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-emerald-600 rounded-full"></div>
                    <span className="text-gray-700">Property Location</span>
                </div>
                <p className="text-gray-500">Hover over markers for details</p>
            </div>

            <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg px-4 py-2">
                <span className="text-sm font-semibold text-gray-900">
                    {properties.length} Properties
                </span>
            </div>
        </div>
    );
}

export default memo(MapViewComponent);

